import React, { useState, useEffect } from 'react';
import { getAddressData } from '../../../../../utils/templates/address';
import { Field } from 'redux-form';
import { CommonDropdownOutlined, OutlinedTextFields } from '@Components';
import { Col, Row } from '../styles';
import { addressFieldKeyMap } from '../utils/constants';
import { AddressTypes } from '../../../../../store.type';
import { renderFuncAddressFields, sortFields } from './methods';
import { BeneAddressFieldsContainer } from './style';
import { useSelector } from 'react-redux';
import { selectNewBeneState } from '@Redux/selectors';
import { BeneficiaryFlowState } from '../../../../beneficiary-flow/types';

interface IAddressFielsProps {
  addressConfig: AddressTypes;
  dispatch: any;
  getAllformVal: any;
  countryCode: string;
  contentCountryCode: string;
  cmsInputFields: any[];
  isBeneAddressFlow?: boolean;
  isBeneSchemaEnabled?: boolean;
}

const AddressFields: React.FC<IAddressFielsProps> = (props): React.ReactElement => {
  const {
    addressConfig,
    getAllformVal,
    contentCountryCode,
    cmsInputFields,
    isBeneAddressFlow,
    isBeneSchemaEnabled
  } = props;
  const [fields, setFields] = useState<any[]>([]);
  const [dimentions, setDimentions] = useState({ width: 0, height: 0 });
  const isMobile = dimentions.width <= 550 ? true : false;
  const { addressConfig: beneAddressConfig } = useSelector(
    selectNewBeneState
  ) as BeneficiaryFlowState;
  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener('resize', updateWindowDimensions);
    return () => {
      window.removeEventListener('resize', updateWindowDimensions);
    };
  }, []);

  useEffect(() => {
    const addressData = getAddressData(addressConfig, true, isBeneAddressFlow, isBeneSchemaEnabled);
    if (!addressData) return;

    if (!isBeneAddressFlow) {
      const fieldArray = addressData?.map((field) => {
        /* Filtering out only required fields on FE, on the basis of constants*/
        const addressFieldFEMapping = addressFieldKeyMap?.[contentCountryCode]?.[field.field];

        // If mapping exists → merge FE config
        if (addressFieldFEMapping) {
          return {
            ...field,
            ...addressFieldFEMapping
          };
        }
        // If no mapping → return API field as-is
        return field;
      });

      setFields(fieldArray || []);
    } else {
      setFields(addressData);
    }
  }, [addressConfig, isBeneAddressFlow, contentCountryCode, isBeneSchemaEnabled, getAllformVal]);

  const updateWindowDimensions = () => {
    setDimentions({ width: window.innerWidth, height: window.innerHeight });
  };

  /* isSecondElementShown flag variable toggles for showing second Element and on next map item we use this to skip that item */
  let isSecondElementShown = false;
  let nextFieldItem;

  return isBeneAddressFlow ? (
    //@ts-ignore
    <BeneAddressFieldsContainer>
      {/* @ts-ignore */}
      {sortFields(beneAddressConfig).map((field) => renderFuncAddressFields(field, getAllformVal))}
    </BeneAddressFieldsContainer>
  ) : (
    <>
      {fields?.map((field, index, fieldArray) => {
        // Only render field if it's visible
        if (field.visible === false) {
          return null;
        }

        switch (field.type) {
          case 'text': {
            if (isSecondElementShown) {
              isSecondElementShown = false;
              return null;
            }
            const addressFieldFEMapping = !isBeneAddressFlow
              ? addressFieldKeyMap?.[contentCountryCode]?.[field.field]
              : null;
            const label =
              (cmsInputFields && cmsInputFields[field.field]?.label) ||
              addressFieldFEMapping?.label;
            const placeholder: string =
              (cmsInputFields && cmsInputFields[field.field]?.placeholder) ||
              addressFieldFEMapping?.placeholder ||
              field.placeholder;

            /* for Mobile and All fields which dont have width externally set up, render those items on separate rows*/
            if (isMobile || !addressFieldFEMapping?.width) {
              return (
                <Row padding={[13, 0, 13, 0]}>
                  <Col width={450}>
                    <Field
                      name={field.field}
                      type="text"
                      parentClassName="form-text-field"
                      component={OutlinedTextFields}
                      maxWidth="450px"
                      validate={field.validate}
                      normalize={field.normalize}
                      label={label}
                      placeholder={placeholder}
                      showLabel={true}
                      disableUnderlineStyling={true}
                      tooltip={getAllformVal && !getAllformVal[field.field] && field.tooltip}
                    />
                  </Col>
                </Row>
              );
            }

            /* If Field has externally setup width, render that field in 1st column, and 2nd field in subsequent column if it exists.
            Assuming 2nd subsequent field has to be on the same row.*/
            if (addressFieldFEMapping?.width) {
              return (
                <Row padding={[13, 0, 13, 0]} flexDirection="row">
                  <Col width={210} isHalfColumn={true}>
                    <Field
                      name={field.field}
                      type="text"
                      parentClassName="form-text-field"
                      component={OutlinedTextFields}
                      maxWidth={`${addressFieldFEMapping?.width || 450}px`}
                      validate={field.validate}
                      normalize={field.normalize}
                      placeholder={placeholder}
                      showLabel={true}
                      disableUnderlineStyling={true}
                      tooltip={getAllformVal && !getAllformVal[field.field] && field.tooltip}
                    />
                  </Col>
                  {index <= fields?.length &&
                    (nextFieldItem = fieldArray[index + 1]) &&
                    (isSecondElementShown = true) && (
                      <Col width={210} isHalfColumn={true}>
                        <Field
                          name={nextFieldItem.field}
                          type="text"
                          parentClassName="form-text-field"
                          component={OutlinedTextFields}
                          maxWidth={`${addressFieldFEMapping?.width || 450}px`}
                          validate={nextFieldItem.validate}
                          normalize={nextFieldItem.normalize}
                          placeholder={
                            (cmsInputFields && cmsInputFields[nextFieldItem.field]?.placeholder) ||
                            addressFieldKeyMap?.[contentCountryCode]?.[nextFieldItem.field]
                              ?.placeholder ||
                            nextFieldItem.placeholder
                          }
                          showLabel={true}
                          disableUnderlineStyling={true}
                          tooltip={
                            getAllformVal &&
                            !getAllformVal[nextFieldItem.field] &&
                            nextFieldItem.tooltip
                          }
                        />
                      </Col>
                    )}
                </Row>
              );
            }
            break;
          }
          case 'dropdown': {
            return (
              <Row padding={[13, 0, 13, 0]}>
                <Col width={450}>
                  <Field
                    bindToThisObject={getAllformVal ? getAllformVal[field.field] : null}
                    name={field.field}
                    type="text"
                    component={CommonDropdownOutlined}
                    className="theme-dropdown"
                    url={`${field.url}`}
                    validate={field.validate}
                    normalize={field.normalize}
                    placeholder={field.placeholder}
                    width={isBeneAddressFlow ? '100%' : ''}
                  />
                </Col>
              </Row>
            );
          }
          default:
            return <></>;
        }
        return <></>;
      })}
    </>
  );
};

export default AddressFields;
